import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

// Get cookie value by name
function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
}

// Set cookie with expiration
function setCookie(name: string, value: string, days: number) {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
}

// Generate or retrieve external ID (unique browser identifier)
function getExternalId(): string {
  const cookieName = 'nutriai_external_id';
  let externalId = getCookie(cookieName);
  
  if (!externalId) {
    // Generate a unique ID: timestamp + random string
    externalId = `${Date.now()}.${Math.random().toString(36).substring(2, 11)}${Math.random().toString(36).substring(2, 11)}`;
    // Save for 2 years (similar to Facebook cookies)
    setCookie(cookieName, externalId, 730);
  }
  
  return externalId;
}

// Get fbp (Facebook browser ID) from _fbp cookie
function getFbp(): string | null {
  return getCookie('_fbp');
}

// Get fbc from _fbc cookie or fbclid URL param
function getFbc(): string | null {
  const fbc = getCookie('_fbc');
  if (fbc) return fbc;
  
  // Check for fbclid in URL and construct fbc
  const params = new URLSearchParams(window.location.search);
  const fbclid = params.get('fbclid');
  if (fbclid) {
    // Format: fb.1.timestamp.fbclid
    return `fb.1.${Date.now()}.${fbclid}`;
  }
  
  return null;
}

export function usePageView() {
  useEffect(() => {
    const sendPageView = async () => {
      try {
        const externalId = getExternalId();
        const fbp = getFbp();
        const fbc = getFbc();

        const { error } = await supabase.functions.invoke('facebook-event', {
          body: {
            event_name: 'PageView',
            event_source_url: window.location.href,
            user_agent: navigator.userAgent,
            fbp: fbp,
            fbc: fbc,
            external_id: externalId,
          },
        });

        if (error) {
          console.error('[PageView] Error sending event:', error);
        }
      } catch (err) {
        console.error('[PageView] Unexpected error:', err);
      }
    };

    // Wait for page to fully load + delay to ensure we're outside LCP measurement
    const deferPageView = () => {
      const runAfterLoad = () => {
        // Delay 2s after load to be completely outside Lighthouse measurement window
        setTimeout(sendPageView, 2000);
      };

      if (document.readyState === 'complete') {
        runAfterLoad();
      } else {
        window.addEventListener('load', runAfterLoad, { once: true });
      }
    };

    deferPageView();
  }, []);
}